import { useSwitcherSdk } from "hooks/useSwitcherSdk";
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState
} from "react";
import HelpIcon from "assets/icons/help-filled.svg?react";
import useClickOutside from "hooks/useClickOutside";
import styles from "./index.module.scss";

export interface TooltipProps {
    type: "CTA" | "info";
    body: JSX.Element | string;
    show: boolean;
    label?: string;
    children?: JSX.Element;
    style?: React.CSSProperties;
    variant?: "default" | "high-contrast" | "dark";
    labelOrientation?: "left" | "right" | "top" | "bottom";
    /** How long in milliseconds before the tooltip overlay disappears */
    hide?: number;
}

type OverlayPlacement = "top" | "bottom";

const VIEWPORT_PADDING = 8;
const OVERLAY_MARGIN = 8;
const ARROW_HALF_WIDTH = 8;

export const TooltipBaseV2 = ({
    type,
    show,
    body,
    label,
    children = <></>,
    style = {},
    variant = "default",
    labelOrientation = "right",
    hide = 2000
}: TooltipProps) => {
    const { isInBrowser } = useSwitcherSdk();

    const ref = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    const [isOpen, setIsOpen] = useState(false);
    const [placement, setPlacement] = useState<OverlayPlacement>("top");
    const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({});
    const [arrowLeft, setArrowLeft] = useState(0);

    const isHoverTrigger = isInBrowser && type !== "CTA";

    const clearHideTimeout = useCallback(() => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = undefined;
        }
    }, []);

    const openOverlay = useCallback(() => {
        clearHideTimeout();
        setIsOpen(true);
    }, [clearHideTimeout]);

    const closeOverlay = useCallback(
        (immediate = false) => {
            clearHideTimeout();
            if (immediate) {
                setIsOpen(false);
                return;
            }
            hideTimeoutRef.current = setTimeout(() => setIsOpen(false), hide);
        },
        [clearHideTimeout, hide]
    );

    useEffect(() => clearHideTimeout, [clearHideTimeout]);

    // rootClose: dismiss when clicking outside of the trigger/overlay
    useClickOutside(ref as React.MutableRefObject<HTMLElement>, () =>
        closeOverlay(true)
    );

    // rootClose: dismiss on Escape
    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeOverlay(true);
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isOpen, closeOverlay]);

    // Manual "auto" placement: prefer bottom, flip to top if there isn't
    // enough room, and clamp horizontally within the viewport - mirrors the
    // flip/preventOverflow Popper modifiers OverlayTrigger used to apply.
    useLayoutEffect(() => {
        if (!isOpen) return;

        const updatePosition = () => {
            if (!triggerRef.current || !overlayRef.current) return;
            const triggerRect = triggerRef.current.getBoundingClientRect();
            const overlayRect = overlayRef.current.getBoundingClientRect();

            const spaceBelow = window.innerHeight - triggerRect.bottom;
            const spaceAbove = triggerRect.top;
            const requiredSpace = overlayRect.height + OVERLAY_MARGIN;
            const nextPlacement: OverlayPlacement =
                spaceBelow >= requiredSpace || spaceBelow >= spaceAbove
                    ? "bottom"
                    : "top";

            const triggerCenter = triggerRect.left + triggerRect.width / 2;
            let left = triggerCenter - overlayRect.width / 2;
            left = Math.min(
                Math.max(left, VIEWPORT_PADDING),
                window.innerWidth - overlayRect.width - VIEWPORT_PADDING
            );

            const top =
                nextPlacement === "bottom"
                    ? triggerRect.bottom + OVERLAY_MARGIN
                    : triggerRect.top - overlayRect.height - OVERLAY_MARGIN;

            setPlacement(nextPlacement);
            // avoid doubling the gap with bootstrap's bs-popover-* margin
            setOverlayStyle({ position: "fixed", top, left, margin: 0 });
            setArrowLeft(triggerCenter - left - ARROW_HALF_WIDTH);
        };

        updatePosition();
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);
        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [isOpen, body]);

    if (!show) return React.cloneElement(children, { style });

    const triggerHandlers: React.HTMLAttributes<HTMLDivElement> = {
        onFocus: openOverlay,
        onBlur: () => closeOverlay()
    };

    if (isHoverTrigger) {
        triggerHandlers.onMouseEnter = openOverlay;
        triggerHandlers.onMouseLeave = () => closeOverlay();
    } else {
        triggerHandlers.onClick = () =>
            isOpen ? closeOverlay(true) : openOverlay();
    }

    return (
        <div ref={ref} style={style}>
            <div
                className={`${styles["container"]} ${styles[labelOrientation]} ${styles[variant]}`}
            >
                <div className={styles["children-container"]}>{children}</div>
                <div
                    className={styles["label-icon-container"]}
                    ref={triggerRef}
                    {...triggerHandlers}
                >
                    <HelpIcon
                        className={`${styles["help-icon"]}`}
                        fill="#007bff"
                    />
                    <small>{label}</small>
                </div>
            </div>
            {isOpen && (
                <div
                    ref={overlayRef}
                    id="popover-basic"
                    role="tooltip"
                    className={`popover bs-popover-${placement}`}
                    style={overlayStyle}
                    onMouseEnter={isHoverTrigger ? clearHideTimeout : undefined}
                    onMouseLeave={
                        isHoverTrigger ? () => closeOverlay() : undefined
                    }
                >
                    <div className="arrow" style={{ left: arrowLeft }} />
                    <div
                        className="popover-body"
                        style={{
                            background:
                                "linear-gradient(0deg, rgba(0, 67, 206, 0.08), rgba(0, 67, 206, 0.08)), #FFFFFF",
                            border: "1px solid rgba(0, 67, 206, 0.24)",
                            borderRadius: "4px",
                            cursor: "default"
                        }}
                    >
                        {body}
                    </div>
                </div>
            )}
        </div>
    );
};
